// Cascading JSON-column classifier.
// PRD: Ralph/JsonColumnSemantics/PRD.md § Slice A2 (engine-native branch),
//      Slice A3 (CHECK constraint branch — added),
//      Slice A4 (MetaInfo declaration branch — added),
//      Slice C4 (inference cache branch — to be added).
//
// JCS-2 precedence: engine-native > CHECK-constraint > MetaInfo declaration >
// inference. Each branch is wired as the corresponding slice ships; this
// router stays the single read-side contract.
//
// Returns `{ kind: 'not-json' }` rather than `undefined` so the read-side helper
// `getJsonClassification` and consumers see a guaranteed lattice value.

import type { SqlEngine } from '../../../sql-grammar/model';
import { NOT_JSON, type JsonClassification } from '../../types';
import { classifyNativeJsonType } from './nativeJsonTypes';

const CHECK_ISJSON: JsonClassification = { kind: 'check-isjson' };
const LOOSE_JSON_DECLARED: JsonClassification = { kind: 'loose-json-declared' };

export type ClassifyJsonColumnOpts = {
  /**
   * True when the engine carries a CHECK constraint that validates this column
   * as JSON (mssql `ISJSON(col) = 1`, oracle `col IS JSON`, sqlite
   * `json_valid(col)`). Detected by per-engine helpers in
   * `./checkIsJsonDetection/`. Ignored when the engine-native branch already
   * matched — JCS-2 precedence puts native first.
   */
  readonly hasIsJsonCheck?: boolean;
  /**
   * True when the user has declared this column as JSON via MetaInfo
   * (`looseJsonColumns`). Consulted only when neither engine-native nor
   * CHECK-constraint detection fires — JCS-2 precedence.
   */
  readonly hasLooseJsonDeclaration?: boolean;
};

export function classifyJsonColumn(
  dataType: string,
  engine: SqlEngine,
  opts?: ClassifyJsonColumnOpts,
): JsonClassification {
  const native = classifyNativeJsonType(dataType, engine);
  if (native) return native;
  if (opts?.hasIsJsonCheck) return CHECK_ISJSON;
  if (opts?.hasLooseJsonDeclaration) return LOOSE_JSON_DECLARED;
  return NOT_JSON;
}
